import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { AvForm, AvInput, AvFeedback } from 'availity-mobx-reactstrap-validation';
import { FormGroup } from 'reactstrap';
import { observable, action } from 'mobx';

import AppState from 'js/stores/AppState';
import AppUser from 'js/stores/AppUser';
import FormState from 'js/stores/forms/LoginSignupDialogForm';
import analytics from 'js/services/Analytics';

import AvGroup from './AvGroup';
import InputText from './InputText';
import Button from 'js/components/common/Button';
import ErrorDisplay from 'js/components/common/ErrorDisplay';
import Validated from './Validated';
import { EyeOpenIcon, LockIcon } from '../../../core/Icons/Eye'

const noop = () => true;

@observer
export default class Form extends Component {
  formState = FormState.create();
  passwordInput = React.createRef();
  tokenInput = React.createRef();
  isMarketing = this.props.marketing || false;

  constructor(props) {
    super(props);
    this.state = {
      showPassword: false,
      passwordFeedback: 'Please enter your password'
    };
  }
  togglePasswordVisibility = () => {
    this.setState(prevState => ({ showPassword: !prevState.showPassword }));
  };
  focusPasswordInput = () => this.passwordInput.current.focus();

  @observable password = '';
  @action
  setPassword(value) {
    this.password = value;
  }
  onChange = (event) => {

    const newPassword = event.target.value;
    this.setPassword(newPassword); // Assuming setPassword updates the observable state

    // Check for validation conditions and set feedback message
    if (!newPassword) {
      this.setState({ passwordFeedback: 'Password is required' });
    } else if (newPassword.length < 6) {
      this.setState({ passwordFeedback: 'Password must be at least 6 characters' });
    } else if (!/^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,72}$/.test(newPassword)) {
      this.setState({ passwordFeedback: 'Password must be 6-72 characters and include a number and a special character' });
    } else {
      this.setState({ passwordFeedback: '' });
    }
  }


  after = response => {

    let tracking;

    analytics.identify(response);
    const prefix = analytics.loginPrefix();

    tracking = analytics
      .track(`${prefix}account_created_email`, {
        'Email': response.email,
        'Signup Method': 'Manuel',
        'Name': this.props.page ? this.props.page.full_name : '',
        'Address': this.props.page ? this.props.page.address : '',
        'Position': AppState.currentPage.indexOf('LandingPage') !== -1 ? 'landing' : 'create-link',
        'Event Initiated by': AppState.currentPage === 'CreatePaymentLinkPage' || AppState.currentPage === 'SellerLandingPage'
          ? 'seller' : 'buyer'
    });

    return tracking.then(() => (this.props.after || noop)(response.email));
  };

  formSubmitted = (event, errors, formValues) => {
    const PSP_SELLER_ALIAS = localStorage.getItem('PSP_SELLER_ALIAS')
    if (PSP_SELLER_ALIAS){
      formValues.psp_principal = PSP_SELLER_ALIAS;
    }else{
      formValues.psp_principal = null;
    }
    // console.info("Page:", this.props.page);
    // console.info("PSP_SELLER_ALIAS:", formValues.psp_principal);
    localStorage.removeItem('PSP_SELLER_ALIAS');
    this.formState.clearError();
    const predicate = this.props.predicate || noop;

    if (predicate() && !errors.length && !this.formState.requestInProgress) {
      (this.props.before || noop)();
      this.formState.setIsRequesting();
      this.formValues = formValues;
      const prefix = analytics.loginPrefix();

      analytics
        .track(`CTA_${prefix}signup_email`, {
          'Email': formValues.email,
          'Signup Method': 'Manual',
          'Name': this.props.page ? this.props.page.full_name : '',
          'Address': this.props.page ? this.props.page.address : '',
          'Position': AppState.currentPage.indexOf('LandingPage') !== -1 ? 'landing' : window.location.href,
          'Event Initiated by': AppState.currentPage === 'CreatePaymentLinkPage' || AppState.currentPage === 'SellerLandingPage'
            ? 'seller' : 'buyer'
        });

      let regType = 'generic';

      if(AppState.currentPage === 'CreatePaymentLinkPage' || AppState.currentPage === 'SellerLandingPage')
        regType = 'seller';
      else if(AppState.currentPage === 'BuyerSignupPage' || AppState.currentPage === 'BuyerLandingPage')
        regType = 'buyer'

      if(this.isMarketing)
        regType = 'mseller';

      formValues.registration_type = regType;

      AppUser.loginUser(null, formValues, this.isMarketing ? 'mktRegister' : 'register')
        .then(this.after)
        .catch(error => {
          const response = error.response ? error.response.data : {};

          if (
            response.errors &&
            Array.isArray(response.errors.login) &&
            response.errors.login[0] === 'validation.displayname'
          ) {
            this.formState.setError(
              'Your display name should be greater than 4 characters, and not have spaces or special characters',
            );
          } else if (response.errors) {
            // data validation error, get the first one and display it
            this.formState.setError(response.errors[Object.keys(response.errors)[0]][0]);
          } else if (response.message) {
            // conflict/other explicit error, get the message and display it
            this.formState.setError(response.message);
          } else {
            // unknown error
            this.formState.setError('Invalid credentials');
          }

          this.formState.setIsRequesting(false);
        });
    }
  };

  render() {
    const { error, requestInProgress } = this.formState;
    const placeholder = this.isMarketing ? 'Enter the email' : 'Enter your email';
    const feedback = this.isMarketing ? 'Please enter the email address.' : 'Please enter your email address.';
    const { showPassword } = this.state;

    return (
      <AvForm onSubmit={this.formSubmitted}>
        <AvGroup>
          <AvInput name="email" placeholder={placeholder} required validate={{ email: true }} autoComplete='email' />
          <AvFeedback>{feedback}</AvFeedback>
        </AvGroup>
        <AvGroup style={{ position: 'relative' }}>
          {!this.isMarketing && (<AvInput
            value={this.password}
            onChange={this.onChange}
            type={showPassword ? 'text' : 'password'}
            name="password"
            autoComplete='new-password'
            placeholder="Choose a password"
            maxLength={72}
            required
            innerRef={this.passwordInput}
            validate={{
              required: { value: true, errorMessage: 'Password is required' },
              pattern: { value: '^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,72}$', errorMessage: 'Password must be 6-72 characters and include a number and a special character' },
              minLength: { value: 6, errorMessage: 'Password must be at least 6 characters' }
            }}
          />)}
          <AvInput
            value={this.props.referral}
            type="hidden"
            name="token"
            placeholder="Token"
            innerRef={this.tokenInput}
          />
          {this.isMarketing && (<AvInput
            value={this.isMarketing}
            type="hidden"
            name="marketing"
          />)}
          {!this.isMarketing && <>
            <InputText value={this.password} onClick={this.focusPasswordInput}>
            at least 6 characters, 1 number and 1 special character
            </InputText>
            <div style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)', cursor: 'pointer' }}>
              {showPassword
                ? <LockIcon onClick={this.togglePasswordVisibility} />
                : <EyeOpenIcon onClick={this.togglePasswordVisibility} />}
            </div>
          </>}
          <AvFeedback> {this.state.passwordFeedback} </AvFeedback>
          <>{this.state.passwordFeedback === '' && <Validated/>}</>
        </AvGroup>

        <FormGroup className="submit">
          <Button type="submit" isLoading={requestInProgress} inverted>
            {this.props.submitLabel || 'Sign Up'}
          </Button>
        </FormGroup>

        <ErrorDisplay>{error}</ErrorDisplay>
      </AvForm>
    );
  }
}
